import React, { useCallback, useEffect, useState, useRef } from 'react';
interface DualRangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
  className?: string;
  inputPrefix?: string;
  inputType?: 'number' | 'text';
}
export function DualRangeSlider({
  min,
  max,
  step = 1,
  value,
  onChange,
  formatLabel = v => v.toString(),
  className = '',
  inputPrefix = '',
  inputType = 'number'
}: DualRangeSliderProps) {
  const [minVal, setMinVal] = useState(value[0]);
  const [maxVal, setMaxVal] = useState(value[1]);
  const [minInput, setMinInput] = useState(value[0].toString());
  const [maxInput, setMaxInput] = useState(value[1].toString());
  const minValRef = useRef(value[0]);
  const maxValRef = useRef(value[1]);
  const range = useRef<HTMLDivElement>(null);
  const getPercent = useCallback((value: number) => Math.round((value - min) / (max - min) * 100), [min, max]);
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);
    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);
  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);
    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);
  useEffect(() => {
    setMinVal(value[0]);
    setMaxVal(value[1]);
    minValRef.current = value[0];
    maxValRef.current = value[1];
    setMinInput(value[0].toString());
    setMaxInput(value[1].toString());
  }, [value]);
  const snapToStep = (val: number) => {
    return Math.round(val / step) * step;
  };
  const handleMinInputBlur = () => {
    let parsed = parseInt(minInput, 10);
    if (isNaN(parsed)) {
      setMinInput(minVal.toString());
      return;
    }
    parsed = snapToStep(parsed);
    parsed = Math.max(min, Math.min(parsed, maxVal - step));
    setMinVal(parsed);
    minValRef.current = parsed;
    setMinInput(parsed.toString());
    onChange([parsed, maxVal]);
  };
  const handleMaxInputBlur = () => {
    let parsed = parseInt(maxInput, 10);
    if (isNaN(parsed)) {
      setMaxInput(maxVal.toString());
      return;
    }
    parsed = snapToStep(parsed);
    parsed = Math.min(max, Math.max(parsed, minVal + step));
    setMaxVal(parsed);
    maxValRef.current = parsed;
    setMaxInput(parsed.toString());
    onChange([minVal, parsed]);
  };
  const handleMinInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleMinInputBlur();
  };
  const handleMaxInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleMaxInputBlur();
  };
  return <div className={`w-full flex flex-col items-center ${className}`} data-id="element-407">
      <div className="relative w-full h-14 flex items-center justify-center px-3" data-id="element-408">
        <input type="range" min={min} max={max} step={step} value={minVal} onChange={event => {
        const value = Math.min(Number(event.target.value), maxVal - step);
        setMinVal(value);
        minValRef.current = value;
        onChange([value, maxVal]);
      }} className="thumb thumb--left pointer-events-none absolute h-0 outline-none z-30" style={{
        zIndex: minVal > max - 100 ? 50 : 30,
        width: 'calc(100% - 24px)'
      }} data-id="element-409" />
        <input type="range" min={min} max={max} step={step} value={maxVal} onChange={event => {
        const value = Math.max(Number(event.target.value), minVal + step);
        setMaxVal(value);
        maxValRef.current = value;
        onChange([minVal, value]);
      }} className="thumb thumb--right pointer-events-none absolute h-0 outline-none z-40" style={{
        width: 'calc(100% - 24px)'
      }} data-id="element-410" />

        <div className="relative w-full" style={{
        margin: '0 12px'
      }} data-id="element-411">
          <div className="absolute h-2 w-full rounded-full bg-gray-200 z-10" data-id="element-412" />
          <div ref={range} className="absolute h-2 rounded-full bg-blue-600 z-20" data-id="element-413" />
        </div>
      </div>

      <div className="w-full flex justify-between items-center mt-1 px-1 gap-2" data-id="element-414">
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1" data-id="element-415">
          {inputPrefix && <span className="text-xs sm:text-sm font-medium text-gray-500" data-id="element-416">
              {inputPrefix}
            </span>}
          <input type="text" inputMode="numeric" value={minInput} onChange={e => setMinInput(e.target.value.replace(/[^0-9]/g, ''))} onBlur={handleMinInputBlur} onKeyDown={handleMinInputKeyDown} className="w-16 sm:w-20 text-xs sm:text-sm font-medium text-gray-700 bg-transparent outline-none text-center" data-id="element-417" />
        </div>
        <span className="text-gray-400 text-sm" data-id="element-418">—</span>
        <div className="flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1" data-id="element-419">
          {inputPrefix && <span className="text-xs sm:text-sm font-medium text-gray-500" data-id="element-420">
              {inputPrefix}
            </span>}
          <input type="text" inputMode="numeric" value={maxInput} onChange={e => setMaxInput(e.target.value.replace(/[^0-9]/g, ''))} onBlur={handleMaxInputBlur} onKeyDown={handleMaxInputKeyDown} className="w-16 sm:w-20 text-xs sm:text-sm font-medium text-gray-700 bg-transparent outline-none text-center" data-id="element-421" />
        </div>
      </div>

      <style data-id="element-422">{`
        .thumb {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
        }
        .thumb::-webkit-slider-runnable-track {
          -webkit-appearance: none;
          height: 0;
        }
        .thumb::-moz-range-track {
          height: 0;
          background: transparent;
          border: none;
        }
        .thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          -webkit-tap-highlight-color: transparent;
          pointer-events: auto;
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background-color: #fff;
          border: 3px solid #2563eb;
          box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          cursor: pointer;
          margin-top: -14px;
          touch-action: none;
        }
        .thumb::-moz-range-thumb {
          pointer-events: auto;
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background-color: #fff;
          border: 3px solid #2563eb;
          box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          cursor: pointer;
          touch-action: none;
        }
        @media (max-width: 640px) {
          .thumb::-webkit-slider-thumb {
            height: 32px;
            width: 32px;
            margin-top: -16px;
            border-width: 3px;
          }
          .thumb::-moz-range-thumb {
            height: 32px;
            width: 32px;
            border-width: 3px;
          }
        }
      `}</style>
    </div>;
}